import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'

import { Typography, Box, CircularProgress, Stack, IconButton, Button } from '@mui/material'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import ImageIcon from '@mui/icons-material/Image'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DownloadIcon from '@mui/icons-material/Download'
import VisibilityIcon from '@mui/icons-material/Visibility'

import {
  blobToBase64,
  getPrivateAssetBlob,
  downloadFile,
  isImageType,
  isPdfType,
  getFileExtension,
  getFileNameWithoutExtension,
} from 'utils/file'
import PdfViewer from 'components/drawer/pdf-viewer/PdfViewer'
import T from 'T'

const EmailAttachmentItem = ({ attachment }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [pdfData, setPdfData] = useState(null)
  const [isPdfLoading, setIsPdfLoading] = useState(false)
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false)
  const { originalFileName, bucketObjectName, contentType } = attachment
  const isImage = isImageType(contentType)
  const isPdf = isPdfType(contentType)

  useEffect(() => {
    if (isImage && bucketObjectName) {
      setIsLoading(true)
      getPrivateAssetBlob(bucketObjectName)
        .then(blobToBase64)
        .then(base64 => setPreviewUrl(base64))
        .catch(() => setPreviewUrl(null))
        .finally(() => setIsLoading(false))
    }
  }, [bucketObjectName, isImage])

  useEffect(() => {
    if (isPdf && bucketObjectName) {
      setIsPdfLoading(true)
      getPrivateAssetBlob(bucketObjectName)
        .then(blobToBase64)
        .then(base64 => {
          // Remove data URL prefix to get raw base64
          const base64Data = base64.split(',')[1]
          setPdfData(base64Data)
        })
        .catch(() => setPdfData(null))
        .finally(() => setIsPdfLoading(false))
    }
  }, [bucketObjectName, isPdf])

  const handleDownload = useCallback(async () => {
    try {
      const blob = await getPrivateAssetBlob(bucketObjectName)
      const extension = getFileExtension(originalFileName)
      downloadFile(blob, originalFileName, extension)
    } catch {
      toast.error(`${T.FAILED_TO_DOWNLOAD} ${originalFileName}`)
    }
  }, [bucketObjectName, originalFileName])

  const handleOpenPdfViewer = () => {
    setIsPdfViewerOpen(true)
  }

  const handleClosePdfViewer = () => {
    setIsPdfViewerOpen(false)
  }

  const getIcon = () => {
    if (isImage) return <ImageIcon fontSize="small" color="primary" />
    if (isPdf) return <PictureAsPdfIcon fontSize="small" color="error" />
    return <InsertDriveFileIcon fontSize="small" color="action" />
  }

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        {isLoading || isPdfLoading ? <CircularProgress size={20} /> : getIcon()}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            component="span"
            onClick={isPdf && pdfData ? handleOpenPdfViewer : handleDownload}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              maxWidth: '100%',
              cursor: 'pointer',
              color: 'primary.main',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {originalFileName}
          </Typography>
        </Box>

        {isPdf && pdfData && (
          <Button size="small" startIcon={<VisibilityIcon />} onClick={handleOpenPdfViewer} variant="text">
            {T.VIEW}
          </Button>
        )}

        <IconButton size="small" onClick={handleDownload} title={T.DOWNLOAD}>
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Stack>

      {isImage && (
        <Box sx={{ mt: 1.5 }}>
          {isLoading && (
            <Box display="flex" alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!isLoading && previewUrl && (
            <Box
              component="img"
              src={previewUrl}
              alt={originalFileName}
              sx={{
                maxWidth: '100%',
                maxHeight: 300,
                objectFit: 'contain',
                display: 'block',
                borderRadius: 0.5,
              }}
            />
          )}
          {!isLoading && !previewUrl && (
            <Typography variant="caption" color="text.secondary">
              {T.FAILED_TO_LOAD_PREVIEW}
            </Typography>
          )}
        </Box>
      )}

      {isPdf && (
        <PdfViewer
          open={isPdfViewerOpen}
          onClose={handleClosePdfViewer}
          pdfData={pdfData}
          isLoading={isPdfLoading}
          fileName={getFileNameWithoutExtension(originalFileName)}
          title={originalFileName}
          type="attachment"
          onDownload={handleDownload}
        />
      )}
    </Box>
  )
}

EmailAttachmentItem.propTypes = {
  attachment: PropTypes.shape({
    originalFileName: PropTypes.string,
    bucketObjectName: PropTypes.string,
    contentType: PropTypes.string,
  }),
}

export default EmailAttachmentItem
